import { z } from "zod";
import { channelObjectTypeIdSchema, channelRecordFilterSchema, dashboardFilterSchema, } from "./channels.js";
import { channelWidgetProblems, channelWidgetSchema } from "./channel-widget-types.js";
import { dashboardLayoutSchema, dashboardWidgetPositionSchema } from "./dashboard-layout.js";
import { widgetResultSchema } from "./widget-results.js";
/*
 * Channel widgets as artifacts (docs/specs/shared-widget-blocks.md, sections 3 and 4). A widget is its
 * own thing with its own history: a spec, never data. An arrangement (a channel's Overview, or one of
 * its dashboard views) places widgets, and is versioned apart from them. A change to either travels in
 * a changeset, which is published whole or not at all and undone as one step.
 *
 * Every placement follows a widget's latest published version, so publishing a widget changes it
 * everywhere it is placed, and a change says where that is before it is made.
 */
/** How many widgets one change may create or revise: a 40-widget dashboard is one call, not 40. */
export const CHANNEL_WIDGET_BATCH_MAX = 40;
/** How many widgets one arrangement may place: what a dashboard holds today. */
export const CHANNEL_WIDGET_PLACEMENTS_MAX = 24;
/**
 * What a widget shows: the data set it reads, and its catalogue type with that type's measures,
 * dimensions, labels and thresholds. The id and position inside `widget` are not read: a widget's id
 * is its artifact's, and where it sits is its placement's.
 */
export const channelWidgetSpecSchema = z.object({
    objectTypeId: channelObjectTypeIdSchema,
    /** A dashboard's type, or one of the Overview's own (channel-widget-types.ts). */
    widget: channelWidgetSchema,
}).strict();
/** A spec as a change gives it: its shape, and what a widget is held to when it is saved (channelWidgetProblems). */
const channelWidgetSpecInputSchema = channelWidgetSpecSchema.superRefine((spec, ctx) => {
    for (const message of channelWidgetProblems(spec.widget))
        ctx.addIssue({ code: "custom", path: ["widget"], message });
});
export const CHANNEL_WIDGET_SURFACES = ["overview", "dashboard"];
/** A reference to a widget the same change creates, where a placement names one that has no id yet. */
export const NEW_WIDGET_PREFIX = "new:";
const widgetRefSchema = z.string().trim().regex(/^[a-z0-9][a-z0-9-]{0,62}$/u, "a short name in lower-case letters, digits and hyphens");
export const channelWidgetPlacementSchema = z.object({
    /** The widget placed: its id, or "new:<ref>" for one the same change creates. */
    widget: z.string().trim().min(1).max(200),
    position: dashboardWidgetPositionSchema,
}).strict();
const placedOnce = (content, ctx) => {
    const seen = new Set();
    for (const [index, placement] of content.placements.entries()) {
        if (seen.has(placement.widget))
            ctx.addIssue({ code: "custom", path: ["placements", index, "widget"], message: `Widget "${placement.widget}" is placed twice.` });
        seen.add(placement.widget);
    }
};
/** What an arrangement holds: its grid, the filters a viewer sets on it, and where each widget sits. */
export const channelWidgetArrangementContentSchema = z.object({
    layout: dashboardLayoutSchema.default({ columns: 12, rowHeight: 120, gap: 8 }),
    filters: z.array(dashboardFilterSchema).max(12).default([]),
    recordFilters: z.array(channelRecordFilterSchema).max(12).optional(),
    placements: z.array(channelWidgetPlacementSchema).max(CHANNEL_WIDGET_PLACEMENTS_MAX),
}).strict().superRefine(placedOnce);
/**
 * An arrangement as the agent gives it: where each widget sits, and the grid and filters only where it
 * means to set them. Whatever it leaves out stays as published (arrangedOver), so placing a widget on a
 * dashboard never clears the dashboard's filters.
 */
export const channelWidgetArrangementChangeSchema = z.object({
    layout: dashboardLayoutSchema.optional(),
    filters: z.array(dashboardFilterSchema).max(12).optional(),
    recordFilters: z.array(channelRecordFilterSchema).max(12).optional(),
    placements: z.array(channelWidgetPlacementSchema).max(CHANNEL_WIDGET_PLACEMENTS_MAX),
}).strict().superRefine(placedOnce);
/** The whole arrangement an agent's change comes to, over what is published now: what the change leaves out is kept. */
export function arrangedOver(change, published) {
    const layout = change.layout ?? published?.layout;
    const recordFilters = change.recordFilters ?? published?.recordFilters;
    return channelWidgetArrangementContentSchema.parse({
        ...(layout ? { layout } : {}),
        filters: change.filters ?? published?.filters ?? [],
        ...(recordFilters ? { recordFilters } : {}),
        placements: change.placements,
    });
}
const wholeChange = (change, ctx) => {
    if (change.create.length + change.revise.length + change.arrange.length === 0) {
        ctx.addIssue({ code: "custom", path: [], message: "A change creates, revises or arranges at least one thing." });
    }
    const refs = new Set();
    for (const [index, created] of change.create.entries()) {
        if (refs.has(created.ref))
            ctx.addIssue({ code: "custom", path: ["create", index, "ref"], message: `Two new widgets are called "${created.ref}".` });
        refs.add(created.ref);
    }
    const revised = new Set();
    for (const [index, revision] of change.revise.entries()) {
        if (revised.has(revision.widgetId))
            ctx.addIssue({ code: "custom", path: ["revise", index, "widgetId"], message: `Widget "${revision.widgetId}" is revised twice.` });
        revised.add(revision.widgetId);
    }
    const arranged = new Set();
    for (const [index, arrangement] of change.arrange.entries()) {
        if ((arrangement.surface === "dashboard") !== (arrangement.viewId !== undefined)) {
            ctx.addIssue({ code: "custom", path: ["arrange", index, "viewId"], message: "A dashboard names its view; the Overview names none." });
        }
        const key = `${arrangement.surface}:${arrangement.viewId ?? ""}`;
        if (arranged.has(key))
            ctx.addIssue({ code: "custom", path: ["arrange", index], message: "The same arrangement is changed twice." });
        arranged.add(key);
        for (const [at, placement] of arrangement.content.placements.entries()) {
            if (placement.widget.startsWith(NEW_WIDGET_PREFIX) && !refs.has(placement.widget.slice(NEW_WIDGET_PREFIX.length))) {
                ctx.addIssue({ code: "custom", path: ["arrange", index, "content", "placements", at, "widget"], message: `No new widget in this change is called "${placement.widget.slice(NEW_WIDGET_PREFIX.length)}".` });
            }
        }
    }
};
const changeFields = {
    idempotencyKey: z.string().trim().min(8).max(200),
    /** What the change does, in a sentence a reviewer reads: "Change the six sales widgets to quarterly". */
    summary: z.string().trim().min(1).max(500),
    create: z.array(z.object({ ref: widgetRefSchema, spec: channelWidgetSpecInputSchema }).strict()).max(CHANNEL_WIDGET_BATCH_MAX).default([]),
};
const revisionFields = {
    widgetId: z.string().trim().min(1).max(200),
    /** The widget's published version this revision was made against. */
    expectedVersionId: z.string().trim().min(1).max(200),
};
/** A revision that gives the widget's whole new spec. */
const wholeRevisionSchema = z.object({ ...revisionFields, spec: channelWidgetSpecInputSchema }).strict();
/**
 * A revision that gives only the settings that change, by name: the tenant lays them over the widget's
 * published settings (revisedSpec), so raising a target never rewrites the measure beside it.
 */
const settingsRevisionSchema = z.object({ ...revisionFields, set: z.record(z.string().trim().min(1).max(80), z.unknown()) }).strict();
const arrangeOf = (content) => z.array(z.object({
    surface: z.enum(CHANNEL_WIDGET_SURFACES),
    /** The dashboard view, for a dashboard; a channel has one Overview, so it names none. */
    viewId: z.string().trim().min(1).max(200).optional(),
    /** The arrangement's published version this was made against; null when it has never been published. */
    expectedVersionId: z.string().trim().min(1).max(200).nullable(),
    content,
}).strict()).max(8).default([]);
/**
 * One change to a channel's widgets: any number created, revised and arranged together. The key makes a
 * retried call do nothing twice. A revision and an arrangement carry the published version they were
 * made against, so a change made over someone else's is refused, never silently laid on top.
 */
export const channelWidgetChangesetInputSchema = z.object({
    ...changeFields, revise: z.array(wholeRevisionSchema).max(CHANNEL_WIDGET_BATCH_MAX).default([]), arrange: arrangeOf(channelWidgetArrangementContentSchema),
}).strict().superRefine(wholeChange);
/**
 * A change as the agent's tools send it: the same, with each arrangement given as the agent gives one, and
 * a revision given whole or as the settings that change. The tenant makes it whole before it is saved.
 */
export const channelWidgetAgentChangeSchema = z.object({
    ...changeFields, revise: z.array(z.union([wholeRevisionSchema, settingsRevisionSchema])).max(CHANNEL_WIDGET_BATCH_MAX).default([]),
    arrange: arrangeOf(channelWidgetArrangementChangeSchema),
}).strict().superRefine(wholeChange);
/** What a widget's settings never are: what the widget is, where it reads from and where it sits are not settings to lay over. */
const NOT_SETTINGS = ["type", "objectTypeId", "id", "position"];
/**
 * A widget's published spec with some of its settings changed. Each setting given replaces that setting
 * whole, and null takes an optional one away. Its type and its data set are not settings: changing either
 * makes a different widget, which a whole revision says. The result is held to what any saved spec is.
 */
export function revisedSpec(published, set) {
    const refused = NOT_SETTINGS.filter((key) => key in set);
    if (refused.length > 0) {
        return { problems: [`${refused.join(" and ")} cannot be set on its own: to change a widget's type or data set, give the whole widget.`] };
    }
    if (Object.keys(set).length === 0)
        return { problems: ["No setting was given to change."] };
    // A dashboard's types drop a key they do not know, so a setting the type does not have is refused here, not lost.
    const type = published.widget.type;
    const settings = Object.keys(channelWidgetSchema.options.find((option) => option.shape.type.value === type).shape)
        .filter((key) => !NOT_SETTINGS.includes(key));
    const unknown = Object.keys(set).filter((key) => !settings.includes(key));
    if (unknown.length > 0) {
        return { problems: [`${unknown.join(", ")} ${unknown.length === 1 ? "is not a setting" : "are not settings"} of a ${type} widget, whose settings are ${settings.join(", ")}.`] };
    }
    const widget = { ...published.widget };
    for (const [key, value] of Object.entries(set)) {
        if (value === null)
            delete widget[key];
        else
            widget[key] = value;
    }
    const parsed = channelWidgetSpecInputSchema.safeParse({ objectTypeId: published.objectTypeId, widget });
    return parsed.success
        ? { spec: parsed.data }
        : { problems: parsed.error.issues.map((issue) => `${issue.path.slice(1).join(".") || "widget"}: ${issue.message}`) };
}
/** What became of a change: published at once (its author is an organisation admin), or kept as a draft for one to accept. */
export const CHANNEL_WIDGET_CHANGE_OUTCOMES = ["published", "draft"];
export const channelWidgetChangesetResultSchema = z.object({
    changesetId: z.string().min(1),
    outcome: z.enum(CHANNEL_WIDGET_CHANGE_OUTCOMES),
    /** True when this key was answered before: nothing was done again, and this is what was done then. */
    replayed: z.boolean(),
    widgets: z.array(z.object({
        /** The name the change gave a widget it created. */
        ref: z.string().optional(),
        widgetId: z.string().min(1), versionId: z.string().min(1), version: z.number().int().positive(),
    }).strict()),
    arrangements: z.array(z.object({
        arrangementId: z.string().min(1), surface: z.enum(CHANNEL_WIDGET_SURFACES), viewId: z.string().optional(),
        versionId: z.string().min(1), version: z.number().int().positive(),
    }).strict()),
    /** The widgets the change removed from the channel, each with the version it was published at: what undoing it brings back. */
    removed: z.array(z.object({ widgetId: z.string().min(1), versionId: z.string().min(1) }).strict()).optional(),
    /** Where the change shows, in words, for the agent to say and a reviewer to read. */
    lands: z.string().min(1),
}).strict();
/** A channel's widget as a list shows it: enough to choose one to revise or place, without its whole spec. */
export const channelWidgetSummarySchema = z.object({
    widgetId: z.string().min(1),
    type: z.string().min(1),
    title: z.string().min(1),
    objectTypeId: z.string().min(1),
    /** Its published version; null for a widget that exists only in drafts. */
    publishedVersionId: z.string().nullable(),
    publishedVersion: z.number().int().positive().nullable(),
    /** Where it is placed: "overview", or a dashboard view's id. */
    placedOn: z.array(z.string().min(1)),
    updatedAt: z.number().int().nonnegative(),
}).strict();
/**
 * Where a change shows, in words: every arrangement a changed widget is placed on follows its latest
 * published version, so a reviewer is told before it is published.
 */
export function channelWidgetChangeLands(placed) {
    const dashboards = placed.dashboards === 0 ? null : placed.dashboards === 1 ? "1 dashboard" : `${placed.dashboards} dashboards`;
    if (placed.overview && dashboards)
        return `Updates on the Overview and ${dashboards}`;
    if (placed.overview)
        return "Updates on the Overview";
    if (dashboards)
        return `Updates on ${dashboards}`;
    return "Not placed anywhere yet";
}
/** Where a draft shows: nowhere until an admin accepts it, and then where its widgets are placed. */
export function channelWidgetDraftLands(lands) {
    if (lands === channelWidgetChangeLands({ overview: false, dashboards: 0 }))
        return "Not shown until an organisation admin accepts it and places it";
    return `Not shown until an organisation admin accepts it; then it ${lands.charAt(0).toLowerCase()}${lands.slice(1)}`;
}
/**
 * The dashboards an arrangement amounts to: its placed widgets, each at its placement's position under
 * its own id, grouped by the data set each reads. A dashboard reads one data set, so an arrangement of
 * widgets over several is computed as one dashboard for each. A placement whose widget has no published
 * spec is left out.
 */
export function arrangedDashboards(content, specs) {
    const groups = new Map();
    for (const placement of content.placements) {
        const spec = specs.get(placement.widget);
        if (!spec)
            continue;
        const widgets = groups.get(spec.objectTypeId) ?? [];
        widgets.push({ ...spec.widget, id: placement.widget, position: placement.position });
        groups.set(spec.objectTypeId, widgets);
    }
    return [...groups].map(([objectTypeId, widgets]) => ({
        objectTypeId,
        definition: {
            version: 1, objectTypeId, layout: content.layout, filters: content.filters,
            ...(content.recordFilters ? { recordFilters: content.recordFilters } : {}), widgets,
        },
    }));
}
/** A widget as an arrangement places it: its latest published spec, with the id and position the arrangement gives it. */
export const channelArrangedWidgetSchema = z.object({
    widgetId: z.string().min(1),
    /** The widget's published version that was computed. */
    versionId: z.string().min(1),
    objectTypeId: channelObjectTypeIdSchema,
    widget: channelWidgetSchema,
}).strict();
/**
 * An arrangement's widgets evaluated for one viewer: what the tenant answers a channel's Overview with.
 * A surface that has never had an arrangement published answers with no widgets and null ids.
 */
export const channelWidgetArrangementResultsSchema = z.object({
    channelId: z.string().min(1),
    surface: z.enum(CHANNEL_WIDGET_SURFACES),
    viewId: z.string().min(1).optional(),
    arrangementId: z.string().min(1).nullable(),
    arrangementVersionId: z.string().min(1).nullable(),
    layout: dashboardLayoutSchema,
    evaluatedAt: z.number().int().nonnegative(),
    timezone: z.string().min(1).max(64),
    widgets: z.array(channelArrangedWidgetSchema).max(CHANNEL_WIDGET_PLACEMENTS_MAX),
    /** Each placed widget's result, by the widget's id. */
    results: z.record(z.string().min(1), widgetResultSchema(z.unknown())),
}).strict();
/**
 * A channel's widgets as the app shows them: every published widget, the Overview as published, and what
 * the reader may do to it. Only an organisation admin arranges the Overview directly (section 4).
 */
export const channelWidgetsResponseSchema = z.object({
    widgets: z.array(channelWidgetSummarySchema),
    /** Each widget's published settings, by widget id, for the admin who edits one by hand; left out for anyone else. */
    specs: z.record(z.string().min(1), channelWidgetSpecSchema).optional(),
    overview: z.object({ versionId: z.string().min(1).nullable(), content: channelWidgetArrangementContentSchema }).strict(),
    /** Whether the reader is an organisation admin now, who arranges the Overview directly. */
    canArrange: z.boolean(),
    /** The latest published change an admin may undo; null when there is none, and always for anyone else. */
    undoable: z.object({ changesetId: z.string().min(1), summary: z.string().min(1) }).strict().nullable(),
}).strict();
/**
 * The settings of a widget an admin changes by hand: its name and description, and the inputs it counts
 * towards or down to. What it measures, over which data set, grouped how, is the Twin's to change: those
 * decide what the number means, and are checked against the data set when they are written.
 */
export const CHANNEL_WIDGET_HAND_SETTINGS = ["title", "description", "target", "unit", "date", "next"];
/**
 * An admin's direct change to a channel's widgets, made in the app: a new arrangement of the Overview,
 * a widget's own settings, removing a widget, or undoing a change.
 */
export const channelOverviewChangeRequestSchema = z.discriminatedUnion("operation", [
    z.object({
        operation: z.literal("revise"),
        idempotencyKey: z.string().trim().min(8).max(200),
        summary: z.string().trim().min(1).max(500),
        widgetId: z.string().trim().min(1).max(200),
        /** The widget's published version the change was made against. */
        expectedVersionId: z.string().trim().min(1).max(200),
        /** Only the settings that change; null takes an optional one away (revisedSpec). */
        set: z.partialRecord(z.enum(CHANNEL_WIDGET_HAND_SETTINGS), z.unknown()),
    }).strict(),
    z.object({
        operation: z.literal("remove"),
        idempotencyKey: z.string().trim().min(8).max(200),
        widgetId: z.string().trim().min(1).max(200),
        expectedVersionId: z.string().trim().min(1).max(200),
    }).strict(),
    z.object({
        operation: z.literal("arrange"),
        idempotencyKey: z.string().trim().min(8).max(200),
        summary: z.string().trim().min(1).max(500),
        /** The Overview's published version the change was made against; null when it has none. */
        expectedVersionId: z.string().trim().min(1).max(200).nullable(),
        content: channelWidgetArrangementContentSchema,
    }).strict(),
    z.object({
        operation: z.literal("undo"),
        idempotencyKey: z.string().trim().min(8).max(200),
        changesetId: z.string().trim().min(1).max(200),
    }).strict(),
]);
